import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API } from '../contexts/AuthContext';
import {
  usePrefs, SOM_OPCOES, MODELO_OPCOES, FILTRO_STATUS_OPCOES, somConfigDe,
} from '../helpers/prefs';
import { estadoPush, ativarPush, desativarPush, detectarPlataforma } from '../push/push';
import { playNotificationSound, unlockNotificationSound } from '../helpers/notificationSound';
import {
  useRespostasRapidas, criarResposta, atualizarResposta, excluirResposta,
} from '../helpers/respostasRapidas';
import { alertarErro } from '../helpers/alerts';
import { noApp, versaoDoApp } from '../helpers/appNativo';

// Segmented control simples (grupo de botões).
function Segmented({ opcoes, valor, onChange }) {
  return (
    <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
      {opcoes.map((o) => {
        const ativo = valor === o.valor;
        return (
          <button
            key={o.valor}
            type="button"
            onClick={() => onChange(o.valor)}
            style={{
              padding: '7px 14px', borderRadius: 10, fontSize: '0.85rem', cursor: 'pointer',
              border: `1px solid ${ativo ? '#3D8B37' : 'var(--portal-border)'}`,
              background: ativo ? '#3D8B37' : 'transparent',
              color: ativo ? '#fff' : 'var(--portal-text)',
              fontWeight: ativo ? 600 : 400,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Bloco({ titulo, icone, descricao, children }) {
  return (
    <section className="content-card" style={{ padding: 18, marginTop: 12 }}>
      <h5 style={{ color: 'var(--portal-text)', marginBottom: descricao ? 2 : 12 }}>
        {icone && <i className={`bi ${icone} me-2`} />}{titulo}
      </h5>
      {descricao && (
        <p style={{ color: 'var(--portal-muted)', fontSize: '0.82rem', marginBottom: 14 }}>{descricao}</p>
      )}
      {children}
    </section>
  );
}

function Linha({ label, hint, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 14, flexWrap: 'wrap', padding: '10px 0',
      borderTop: '1px solid var(--portal-border)',
    }}>
      <div style={{ minWidth: 160 }}>
        <div style={{ color: 'var(--portal-text)', fontSize: '0.9rem', fontWeight: 500 }}>{label}</div>
        {hint && <div style={{ color: 'var(--portal-muted)', fontSize: '0.78rem' }}>{hint}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

// Assinatura das mensagens: o nome de quem atende ou a função dela.
//
// Fica no servidor (e não em prefs do navegador) porque quem monta o texto que
// vai pro WhatsApp é o backend — e porque a escolha tem que seguir a pessoa em
// qualquer aparelho, não o aparelho.
function Assinatura() {
  const [modo, setModo] = useState(null); // null = ainda carregando
  const [previa, setPrevia] = useState({ nome: null, funcoes: [] });

  useEffect(() => {
    axios.get(`${API}/preferencias`)
      .then((r) => {
        const d = r.data?.data || {};
        setModo(d.assinatura_modo || 'nome');
        setPrevia(d.assinatura_previa || { nome: null, funcoes: [] });
      })
      .catch(() => setModo('nome'));
  }, []);

  const escolher = async (v) => {
    const anterior = modo;
    setModo(v);
    try {
      await axios.put(`${API}/preferencias`, { assinatura_modo: v });
    } catch (e) {
      setModo(anterior); // a tela não pode ficar mostrando o que não salvou
      alertarErro(e, 'Salvar a assinatura');
    }
  };

  const funcoes = previa.funcoes || [];
  // O que o cliente vai ver, com os dados reais da pessoa — escolher no escuro
  // é o que faz alguém descobrir o resultado só depois de mandar pro cliente.
  const exemplo = modo === 'funcao'
    ? (funcoes[0] || null)
    : (previa.nome || null);

  let observacao = null;
  if (modo === 'funcao' && funcoes.length > 1) {
    observacao = `Você está em mais de uma fila: a assinatura acompanha o atendimento — ${funcoes.join(' ou ')}.`;
  } else if (modo === 'funcao' && funcoes.length === 0) {
    observacao = 'Suas filas ainda não têm função marcada, então vai sair o nome da fila. Peça pro gestor marcar a função da fila em Filas.';
  }

  return (
    <>
      <Linha
        label="Quem assina"
        hint="Aparece em negrito antes do texto, na mensagem que o cliente recebe."
      >
        <Segmented
          opcoes={[{ valor: 'nome', label: 'Meu nome' }, { valor: 'funcao', label: 'Minha função' }]}
          valor={modo}
          onChange={escolher}
        />
      </Linha>
      {modo && (
        <div style={{
          borderTop: '1px solid var(--portal-border)', paddingTop: 10,
          color: 'var(--portal-muted)', fontSize: '0.8rem',
        }}>
          {exemplo ? (
            <>
              O cliente vê:{' '}
              <span style={{ color: 'var(--portal-text)', fontWeight: 700 }}>{exemplo}:</span>
              {' '}seguido da sua mensagem.
            </>
          ) : (
            'A mensagem vai sair sem assinatura.'
          )}
          {observacao && <div style={{ marginTop: 4 }}>{observacao}</div>}
        </div>
      )}
    </>
  );
}

// Um cartão de passos de instalação (iOS ou Android).
function BoxPWA({ titulo, icone, destaque, passos }) {
  return (
    <div style={{
      flex: 1, minWidth: 230, padding: 12, borderRadius: 10,
      border: `1px solid ${destaque ? '#3D8B37' : 'var(--portal-border)'}`,
      background: destaque ? 'rgba(61,139,55,0.08)' : 'transparent',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, marginBottom: 8, color: 'var(--portal-text)', fontSize: '0.9rem' }}>
        <i className={`bi ${icone}`} />{titulo}
        {destaque && <span style={{ fontSize: '0.66rem', color: '#3D8B37', border: '1px solid #3D8B37', borderRadius: 8, padding: '0 6px' }}>seu aparelho</span>}
      </div>
      <ol style={{ margin: 0, paddingLeft: 18, fontSize: '0.83rem', color: 'var(--portal-muted)', lineHeight: 1.65 }}>
        {passos.map((p, i) => <li key={i}>{p}</li>)}
      </ol>
    </div>
  );
}

// Guia de instalação do PWA (iPhone/Safari e Android/Chrome).
// O link do APK é FIXO por construção: `fio.apk` no servidor é um ponteiro pra
// última versão, reescrito a cada build. Por isso o QR pode ser uma imagem
// estática no repositório — não muda nunca, e não custa biblioteca nenhuma em
// tempo de execução.
const URL_APK = 'https://fio.centraomateriais.com/app/fio.apk';

function InstalarApp() {
  const { isIOS, isStandalone } = detectarPlataforma();
  const isAndroid = /Android/.test(navigator.userAgent);

  // Quem já está DENTRO do app não tem o que instalar. Vale mais que um "já
  // instalado" genérico: mostra a versão, que é a primeira coisa que o suporte
  // pergunta quando alguém diz que algo não funciona.
  if (noApp()) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', color: 'var(--portal-text)', fontSize: '0.88rem' }}>
        <i className="bi bi-check-circle-fill" style={{ color: '#3D8B37', fontSize: '1.2rem' }} />
        <span>
          Você está no <b>app do Fio</b>
          {versaoDoApp() ? <> — versão <b>{versaoDoApp()}</b></> : null}.
          Ele se atualiza sozinho junto com o Fio.
        </span>
      </div>
    );
  }

  return (
    <>
      {/* Android tem app de verdade; iPhone não, por decisão (o Fio é interno e
          vai por APK). Por isso o Android vê o APK primeiro e o iPhone vê o
          caminho do Safari — e não os dois lados uma lista de passos que não
          serve pro aparelho que a pessoa tem na mão. */}
      {!isIOS && (
        <div style={{
          display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center',
          padding: 14, borderRadius: 10, marginBottom: 14,
          background: 'rgba(61,139,55,0.08)', border: '1px solid rgba(61,139,55,0.3)',
        }}>
          <img
            src="/qr-instalar-app.svg"
            alt={`QR code para baixar o app do Fio em ${URL_APK}`}
            width={132}
            height={132}
            style={{ borderRadius: 6, background: '#fff', padding: 6, flexShrink: 0 }}
          />
          <div style={{ flex: '1 1 260px', minWidth: 0 }}>
            <div style={{ color: 'var(--portal-text)', fontWeight: 600, marginBottom: 4 }}>
              <i className="bi bi-android2 me-2" style={{ color: '#3D8B37' }} />
              App do Fio para Android
            </div>
            <p style={{ color: 'var(--portal-muted)', fontSize: '0.83rem', marginBottom: 8 }}>
              {isAndroid
                ? 'Toque no botão abaixo pra baixar. O QR serve pra instalar em outro aparelho.'
                : 'Aponte a câmera do celular pro código. O link é sempre a última versão.'}
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <a
                href={URL_APK}
                className="btn btn-sm"
                style={{ background: '#3D8B37', color: '#fff', border: 'none' }}
              >
                <i className="bi bi-download me-1" />
                Baixar o app
              </a>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  navigator.clipboard?.writeText(URL_APK)
                    .then(() => Swal.fire({ icon: 'success', title: 'Link copiado', timer: 1400, showConfirmButton: false }))
                    .catch(() => {});
                }}
              >
                <i className="bi bi-link-45deg me-1" />
                Copiar link
              </button>
            </div>
            {/* Sem este aviso o alerta do Android parece defeito, e a pessoa
                desiste no meio da instalação achando que o arquivo é ruim. */}
            <p style={{ color: 'var(--portal-muted)', fontSize: '0.78rem', marginTop: 10, marginBottom: 0 }}>
              O Android vai avisar sobre <b>&quot;instalar apps desconhecidos&quot;</b>. É normal e acontece
              uma vez por aparelho: o app é interno do Centrão e não passa pela loja.
            </p>
          </div>
        </div>
      )}

      {isStandalone ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', color: 'var(--portal-text)', fontSize: '0.88rem' }}>
          <i className="bi bi-check-circle-fill" style={{ color: '#3D8B37', fontSize: '1.2rem' }} />
          Fio instalado na tela inicial deste aparelho. 👍
        </div>
      ) : (
        <>
          <p style={{ color: 'var(--portal-muted)', fontSize: '0.83rem', marginTop: 4, marginBottom: 10 }}>
            {isIOS
              ? 'No iPhone não existe o app instalável — o Fio é interno e vai por APK no Android. Instale na tela inicial pra usar em tela cheia; a notificação só funciona depois de instalar.'
              : 'Sem instalar nada: dá pra usar o Fio pelo navegador, e adicioná-lo à tela inicial.'}
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <BoxPWA
              titulo="iPhone / iPad (Safari)"
              icone="bi-apple"
              destaque={isIOS}
              passos={[
                'Abra o Fio no Safari (precisa ser o Safari).',
                'Toque no botão Compartilhar (o quadrado com a seta pra cima).',
                'Role e escolha "Adicionar à Tela de Início".',
                'Toque em "Adicionar". Abra o app pelo ícone novo na tela.',
              ]}
            />
            <BoxPWA
              titulo="Android (Chrome)"
              icone="bi-android2"
              /* Nunca destacado: no Android o caminho é o APK acima. Deixar
                 este em evidência ofereceria duas portas pra mesma coisa. */
              destaque={false}
              passos={[
                'Abra o Fio no Chrome.',
                'Toque no menu (⋮) no canto superior direito.',
                'Escolha "Instalar app" (ou "Adicionar à tela inicial").',
                'Confirme. Abra o app pelo ícone novo na tela.',
              ]}
            />
          </div>
        </>
      )}
    </>
  );
}

// Filtro padrão da tela de Conversas (por usuário): qual status e qual fila já
// vêm selecionados ao abrir. Ex.: sempre abrir em "Pendente".
function FiltrosPadrao() {
  const [prefs, setPrefs] = usePrefs();
  const [filas, setFilas] = useState([]);
  useEffect(() => {
    axios.get(`${API}/setores`, { params: { ativos: 'true' } })
      .then((r) => setFilas(r.data?.data || []))
      .catch(() => {});
  }, []);

  const selectStyle = {
    padding: '7px 10px', borderRadius: 8, border: '1px solid var(--portal-border)',
    background: 'var(--portal-surface)', color: 'var(--portal-text)', fontSize: '0.85rem', minWidth: 160,
  };

  return (
    <>
      <Linha label="Status" hint="Filtro de status ao abrir Conversas.">
        <Segmented opcoes={FILTRO_STATUS_OPCOES} valor={prefs.filtroStatus} onChange={(v) => setPrefs({ filtroStatus: v })} />
      </Linha>
      <Linha label="Fila" hint="Fila que já vem selecionada ao abrir. A tela lembra a última fila que você clicou — isto aqui é só o ponto de partida.">
        <select style={selectStyle} value={prefs.filtroFila} onChange={(e) => setPrefs({ filtroFila: e.target.value })}>
          <option value="">Todas</option>
          <option value="__pendente__">Sem fila (pendente)</option>
          {/* Guarda o ID: fila pertence a um canal, e "Caixa" pode existir em
              mais de um número — por nome, um canal atropelaria o outro. */}
          {filas.map((f) => (
            <option key={f.id} value={String(f.id)}>
              {f.canal_nome ? `${f.nome} · ${f.canal_nome}` : f.nome}
            </option>
          ))}
        </select>
      </Linha>
    </>
  );
}

// CRUD de respostas rápidas (server-side, seguem o login).
function GerenciarRespostas() {
  const lista = useRespostasRapidas();
  const [novoAtalho, setNovoAtalho] = useState('');
  const [novoTexto, setNovoTexto] = useState('');
  const [editando, setEditando] = useState(null);
  const [busy, setBusy] = useState(false);

  const inputStyle = {
    padding: '7px 10px', borderRadius: 8, border: '1px solid var(--portal-border)',
    background: 'var(--portal-surface)', color: 'var(--portal-text)', fontSize: '0.85rem',
  };

  const adicionar = async () => {
    const a = novoAtalho.trim().replace(/\s+/g, '');
    const t = novoTexto.trim();
    if (!a || !t) return;
    setBusy(true);
    try { await criarResposta(a, t); setNovoAtalho(''); setNovoTexto(''); }
    catch (e) { alertarErro(e, 'Não deu pra concluir'); }
    finally { setBusy(false); }
  };

  const salvarEdicao = async () => {
    const a = (editando.atalho || '').trim().replace(/\s+/g, '');
    const t = (editando.texto || '').trim();
    if (!a || !t) return;
    setBusy(true);
    try { await atualizarResposta(editando.id, a, t); setEditando(null); }
    catch (e) { alertarErro(e, 'Não deu pra concluir'); }
    finally { setBusy(false); }
  };

  const excluir = async (id) => {
    const c = await Swal.fire({
      icon: 'warning', title: 'Excluir resposta?', showCancelButton: true,
      confirmButtonText: 'Excluir', cancelButtonText: 'Cancelar',
    });
    if (!c.isConfirmed) return;
    try { await excluirResposta(id); }
    catch (e) { alertarErro(e, 'Não deu pra concluir'); }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start', padding: '12px 0 4px' }}>
        <input value={novoAtalho} onChange={(e) => setNovoAtalho(e.target.value)} placeholder="atalho (ex.: boleto)" style={{ ...inputStyle, width: 140 }} />
        <textarea value={novoTexto} onChange={(e) => setNovoTexto(e.target.value)} placeholder="texto pronto" rows={2} style={{ ...inputStyle, flex: 1, minWidth: 200, resize: 'vertical' }} />
        <button type="button" onClick={adicionar} disabled={busy || !novoAtalho.trim() || !novoTexto.trim()} className="btn btn-sm" style={{ background: '#3D8B37', color: '#fff', border: 'none' }}>
          <i className="bi bi-plus-circle me-1" />Adicionar
        </button>
      </div>
      {lista.length === 0 ? (
        <div style={{ color: 'var(--portal-muted)', fontSize: '0.82rem', padding: '8px 0' }}>
          Nenhuma resposta ainda. Crie a primeira acima.
        </div>
      ) : lista.map((r) => (
        <div key={r.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 0', borderTop: '1px solid var(--portal-border)' }}>
          {editando && editando.id === r.id ? (
            <>
              <input value={editando.atalho} onChange={(e) => setEditando({ ...editando, atalho: e.target.value })} style={{ ...inputStyle, width: 140 }} />
              <textarea value={editando.texto} onChange={(e) => setEditando({ ...editando, texto: e.target.value })} rows={2} style={{ ...inputStyle, flex: 1, minWidth: 200, resize: 'vertical' }} />
              <button type="button" onClick={salvarEdicao} disabled={busy} className="btn btn-sm btn-outline-success" title="Salvar"><i className="bi bi-check-lg" /></button>
              <button type="button" onClick={() => setEditando(null)} className="btn btn-sm btn-outline-secondary" title="Cancelar"><i className="bi bi-x-lg" /></button>
            </>
          ) : (
            <>
              <span style={{ color: '#3D8B37', fontWeight: 700, minWidth: 90, fontSize: '0.85rem' }}>/{r.atalho}</span>
              <span style={{ flex: 1, color: 'var(--portal-text)', fontSize: '0.85rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{r.texto}</span>
              <button type="button" onClick={() => setEditando({ id: r.id, atalho: r.atalho, texto: r.texto })} className="btn btn-sm btn-outline-secondary" title="Editar"><i className="bi bi-pencil" /></button>
              <button type="button" onClick={() => excluir(r.id)} className="btn btn-sm btn-outline-danger" title="Excluir"><i className="bi bi-trash" /></button>
            </>
          )}
        </div>
      ))}
    </>
  );
}

export default function Configuracoes() {
  const [prefs, setPrefs] = usePrefs();
  const [push, setPush] = useState(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushGrupos, setPushGrupos] = useState(false);

  const atualizarPush = () => estadoPush().then(setPush).catch(() => {});
  useEffect(() => { atualizarPush(); }, []);
  useEffect(() => {
    axios.get(`${API}/notificacoes/preferencias`)
      .then((r) => setPushGrupos(Boolean(r.data?.data?.push_grupos)))
      .catch(() => {});
  }, []);
  const toggleGrupos = async (v) => {
    setPushGrupos(v);
    try {
      const atual = (await axios.get(`${API}/notificacoes/preferencias`)).data?.data || {};
      await axios.put(`${API}/notificacoes/preferencias`, {
        push: atual.push !== false, email: atual.email !== false, push_grupos: v,
      });
    } catch (e) { /* silencioso */ }
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      const inscrito = push?.inscrito && push?.permissao === 'granted';
      if (inscrito) await desativarPush();
      else await ativarPush();
      await atualizarPush();
    } catch (e) {
      Swal.fire({ icon: 'warning', title: 'Notificações', text: e.message });
    } finally {
      setPushBusy(false);
    }
  };

  const testarSom = async () => {
    await unlockNotificationSound();
    playNotificationSound({ ...somConfigDe(prefs), ativo: true }).catch(() => {});
  };

  const inscrito = push?.inscrito && push?.permissao === 'granted';

  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">Fio</span>
          <h1>Configurações</h1>
          <p>Ajuste a sua experiência — vale só pra você, neste aparelho.</p>
        </div>
      </section>

      {/* Aparência */}
      <Bloco titulo="Aparência" icone="bi-palette" descricao="Tema da tela.">
        <Linha label="Tema" hint="Claro ou escuro.">
          <Segmented
            opcoes={[{ valor: 'escuro', label: 'Escuro' }, { valor: 'claro', label: 'Claro' }]}
            valor={prefs.tema}
            onChange={(v) => setPrefs({ tema: v })}
          />
        </Linha>
      </Bloco>

      {/* Instalar o app — APK no Android, tela inicial no iPhone */}
      <Bloco titulo="Instalar o app" icone="bi-phone" descricao="O Fio no celular, com aviso mesmo com o app fechado.">
        <InstalarApp />
      </Bloco>

      {/* Notificações */}
      <Bloco titulo="Notificações" icone="bi-bell" descricao="Como você quer ser avisado quando chega mensagem.">
        <Linha
          label="Avisos neste aparelho"
          hint={inscrito ? 'Ligados — você recebe avisos mesmo com o app fechado.' : 'Desligados.'}
        >
          {push && !push.suportado ? (
            push.iosPrecisaInstalar ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => Swal.fire({
                  icon: 'info',
                  title: 'Ativar no iPhone',
                  html: 'Instale o app primeiro:<br><b>Compartilhar → Adicionar à Tela de Início</b>.<br>Depois abra pelo ícone e ative aqui.',
                })}
              >
                Como ativar no iPhone
              </button>
            ) : (
              <span style={{ color: 'var(--portal-muted)', fontSize: '0.82rem' }}>
                Não suportado neste navegador.
              </span>
            )
          ) : (
            <button
              type="button"
              className="btn btn-sm"
              onClick={togglePush}
              disabled={pushBusy}
              style={inscrito
                ? { background: 'transparent', color: '#3D8B37', border: '1px solid #3D8B37' }
                : { background: '#3D8B37', color: '#fff', border: 'none' }}
            >
              <i className={`bi ${inscrito ? 'bi-bell-slash' : 'bi-bell-fill'} me-1`} />
              {pushBusy ? '...' : (inscrito ? 'Desativar' : 'Ativar avisos')}
            </button>
          )}
        </Linha>

        <Linha label="Avisar sobre grupos" hint="Push quando chega mensagem nos grupos de WhatsApp. Desligado, você só vê o contador vermelho.">
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => toggleGrupos(!pushGrupos)}
            style={pushGrupos
              ? { background: '#3D8B37', color: '#fff', border: 'none' }
              : { background: 'transparent', color: '#3D8B37', border: '1px solid #3D8B37' }}
          >
            <i className={`bi ${pushGrupos ? 'bi-bell-fill' : 'bi-bell-slash'} me-1`} />
            {pushGrupos ? 'Ligado' : 'Desligado'}
          </button>
        </Linha>

        <Linha label="Modelo de aviso" hint="O que acontece quando chega mensagem.">
          <Segmented opcoes={MODELO_OPCOES} valor={prefs.modeloNotif} onChange={(v) => setPrefs({ modeloNotif: v })} />
        </Linha>

        <Linha label="Som" hint="Toque do aviso.">
          <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Segmented opcoes={SOM_OPCOES} valor={prefs.som} onChange={(v) => setPrefs({ som: v })} />
            <button type="button" className="btn btn-sm btn-outline-info" onClick={testarSom}>
              <i className="bi bi-play-fill me-1" />Testar
            </button>
          </div>
        </Linha>

        <Linha label="Volume" hint={`${prefs.volume}%`}>
          <input
            type="range"
            min="0"
            max="100"
            value={prefs.volume}
            onChange={(e) => setPrefs({ volume: Number(e.target.value) })}
            style={{ width: 180, accentColor: '#3D8B37' }}
          />
        </Linha>
      </Bloco>

      {/* Conversa */}
      <Bloco titulo="Conversa" icone="bi-chat-dots" descricao="Comportamento ao digitar.">
        <Linha label="Tecla Enter" hint={prefs.enterEnvia ? 'Enter envia; Shift+Enter quebra linha.' : 'Enter quebra linha; envia só no botão.'}>
          <Segmented
            opcoes={[{ valor: true, label: 'Enter envia' }, { valor: false, label: 'Enter quebra linha' }]}
            valor={prefs.enterEnvia}
            onChange={(v) => setPrefs({ enterEnvia: v })}
          />
        </Linha>
      </Bloco>

      {/* Assinatura das mensagens (por usuário, no servidor) */}
      <Bloco
        titulo="Assinatura"
        icone="bi-pen"
        descricao="O que aparece antes do texto nas mensagens que você manda pro cliente."
      >
        <Assinatura />
      </Bloco>

      {/* Filtro padrão da tela de Conversas */}
      <Bloco titulo="Filtro padrão" icone="bi-funnel" descricao="Como a tela de Conversas já abre pra você.">
        <FiltrosPadrao />
      </Bloco>

      {/* Respostas rápidas (CRUD, por usuário) */}
      <Bloco
        titulo="Respostas rápidas"
        icone="bi-lightning-charge"
        descricao="Textos prontos pra inserir no chat com o ⚡ ou digitando /atalho. Seguem o seu login, em qualquer aparelho."
      >
        <GerenciarRespostas />
      </Bloco>
    </div>
  );
}
